import { debounce } from "@moe/priv/utils";
import { useEffect, useMemo, useRef } from "react";

/**
 * A hook that returns a debounced version of the provided callback function.
 * Copied from: https://www.developerway.com/posts/debouncing-in-react
 *
 * @param callback The function to be debounced.
 * @param delay The delay time in milliseconds.
 * @returns A debounced version of the provided callback function with the same type signature.
 */
export function useDebounceFn<T extends (...args: any[]) => any>(callback: T, delay: number): T {
  const ref = useRef<T>(callback);

  useEffect(() => {
    ref.current = callback;
  }, [callback]);

  const debouncedCallback = useMemo(() => {
    const fn = (...args: Parameters<T>) => {
      ref.current?.(...args);
    };

    return debounce(fn, delay) as T;
  }, [delay]);

  return debouncedCallback;
}
